import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import {
  Box,
  Card,
  Flex,
  Heading,
  Hide,
  Link,
  Text,
  mediaQueries
} from 'pcln-design-system'
import { ImageTile } from '@pcln/content-tile'
import { AnalyticsOnClick } from '@pcln/react-analytics'
import { PriceBox, StrikePrice } from '@pcln/price-box'
import ArrowButton from './ArrowButton'

const HideableCard = styled(Card)`
  ${mediaQueries[0]} {
    display: ${props => (props.shouldShow ? 'block' : 'none')};
  }
`

HideableCard.displayName = 'HideableCard'

const RemainingWidthFlex = styled(Flex)`
  flex-grow: 1;
`

const NoWrapText = styled(Text)`
  ${mediaQueries[1]} {
    white-space: nowrap;
  }
`

const getImageTile = (
  data,
  isUsingNavArrows,
  handleNavOnClick,
  isUsingPricePrefix,
  isUsingPriceType,
  isOpaque,
  isTrips
) => (
  <Box>
    <ImageTile
      height={data.imageHeight}
      image={data.image}
      imageWebP={data.imageWebP}
      navArray={data.navArray}
      handleNavOnClick={isUsingNavArrows && handleNavOnClick}
      isOpaque={isOpaque}
      alt={data.cityName + 'image'}
    />
    <Text color='text' p={2}>
      <Flex justifyContent='space-between' alignItems='baseline'>
        <Text fontSize={0}>{data.productName}</Text>
        {!!data.strikePrice && (
          <StrikePrice price={data.strikePrice} size={'small'} />
        )}
      </Flex>
      <Flex justifyContent='space-between' alignItems='baseline'>
        <Box>
          <NoWrapText fontSize={3} fontWeight='bold'>
            {data.cityName}
          </NoWrapText>
          <Text fontSize={0} mt={1}>
            {data.description}
          </Text>
        </Box>
        {!!data.priceAmount && (
          <NoWrapText mt='1px' ml={2}>
            <Flex alignItems='baseline'>
              {isUsingPricePrefix && (
                <Text.span mx={2} color='secondary' fontSize={4} fontWeight='bold'>
                  {data.pricePrefix}
                </Text.span>
              )}
              <PriceBox
                priceSize='small'
                price={data.priceAmount}
                priceColor='green'
                priceType={isUsingPriceType && 'per person'}
                alignItems='right'
                toFixDecimal={0}
              />
            </Flex>
          </NoWrapText>
        )}
      </Flex>
    </Text>
  </Box>
)

// TODO: improve the list of params using "deeper" React pattern
const parseDealTiles = (
  dataArray,
  isUsingNavArrows,
  handleNavOnClick,
  isUsingPriceType,
  isUsingPricePrefix,
  isOpaque,
  position,
  numDealsToDisplay,
  isTrips
) => {
  return dataArray.map((data, index) => (
    <AnalyticsOnClick action={data.gaAction} label={data.gaLabel}>
      <HideableCard
        shouldShow={index >= position && index < position + numDealsToDisplay}
        mx={[0, 2]}
        mb={index === dataArray.length - 1 ? 0 : [3, 0]}
        width={[1, 1 / numDealsToDisplay]}
        key={`deal-tile-${index}`}
      >
        {isTrips ? (
          <Box onClick={data.onTileClick} data-autobot-element-id={data.id}>
            {getImageTile(
              data,
              isUsingNavArrows,
              handleNavOnClick,
              isUsingPricePrefix,
              isUsingPriceType,
              isOpaque,
              isTrips
            )}
          </Box>
        ) : (
          <Link
            data-autobot-element-id={data.id}
            href={data.navArray[0].href}
            target='_blank'
            style={{ textDecorationLine: 'none' }}
          >
            {getImageTile(
              data,
              isUsingNavArrows,
              handleNavOnClick,
              isUsingPricePrefix,
              isUsingPriceType,
              isOpaque,
              false
            )}
          </Link>
        )}
      </HideableCard>
    </AnalyticsOnClick>
  ))
}

class ScrollableDealModule extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      position: 0,
      numDealsToDisplay:
        window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1
    }

    this.handleLeftArrowClick = this.updatePosition.bind(this, false)
    this.handleRightArrowClick = this.updatePosition.bind(this, true)
    this.handleResize = this.handleResize.bind(this)
  }

  componentDidMount () {
    window.addEventListener('resize', this.handleResize)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize () {
    const screenSize = window.innerWidth
    if (screenSize >= 1024 && this.state.numDealsToDisplay !== 3) {
      this.setState({ numDealsToDisplay: 3 })
    } else if (
      screenSize >= 640 &&
      screenSize < 1024 &&
      this.state.numDealsToDisplay !== 2
    ) {
      this.setState({ numDealsToDisplay: 2 })
    } else if (screenSize < 640 && this.state.numDealsToDisplay !== 1) {
      this.setState({ numDealsToDisplay: 1 })
    }
  }

  updatePosition (shouldAdd) {
    if (shouldAdd) {
      const maxIndex =
        this.props.content.dataArray.length - this.state.numDealsToDisplay
      const nextPosition = this.state.position + this.state.numDealsToDisplay
      const position = Math.min(nextPosition, maxIndex)
      this.setState({ position })
    } else {
      const nextPosition = this.state.position - this.state.numDealsToDisplay
      const position = Math.max(nextPosition, 0)
      this.setState({ position })
    }
  }

  render () {
    const {
      content,
      isUsingNavArrows,
      handleNavOnClick,
      isUsingPriceType,
      isUsingPricePrefix,
      isOpaque,
      isTrips
    } = this.props

    return (
      <Box>
        <Heading fontSize={4} fontWeight='bold' p={2}>
          <Hide xs>{content.heading}</Hide>
          <Hide sm md lg xl xxl>
            {content.headingMobile}
          </Hide>
        </Heading>
        <Flex justifyContent='center' alignItems='center'>
          {isUsingNavArrows && (
            <Hide xs>
              <ArrowButton
                aria-label='Previous'
                direction='left'
                mr={3}
                disabled={this.state.position === 0}
                onClick={this.handleLeftArrowClick}
              />
            </Hide>
          )}
          <RemainingWidthFlex flexDirection={['column', 'row']} mx={2}>
            {parseDealTiles(
              content.dataArray,
              isUsingNavArrows,
              handleNavOnClick,
              isUsingPriceType,
              isUsingPricePrefix,
              isOpaque,
              this.state.position,
              this.state.numDealsToDisplay,
              isTrips
            )}
          </RemainingWidthFlex>
          {isUsingNavArrows && (
            <Hide xs>
              <ArrowButton
                aria-label='Next'
                direction='right'
                disabled={
                  this.state.position >=
                  content.dataArray.length - this.state.numDealsToDisplay
                }
                onClick={this.handleRightArrowClick}
              />
            </Hide>
          )}
        </Flex>
      </Box>
    )
  }
}

ScrollableDealModule.displayName = 'ScrollableDealModule'

ScrollableDealModule.defaultProps = {
  isUsingNavArrows: true,
  isUsingPriceType: true,
  isUsingPricePrefix: false,
  isOpaque: true,
  isTrips: false
}

ScrollableDealModule.propTypes = {
  content: PropTypes.object.isRequired,
  isUsingNavArrows: PropTypes.bool,
  handleNavOnClick: PropTypes.func,
  isUsingPriceType: PropTypes.bool,
  isUsingPricePrefix: PropTypes.bool,
  isOpaque: PropTypes.bool,
  isTrips: PropTypes.bool
}

export default ScrollableDealModule
